import { CaretRight, MagnifyingGlass, X } from "@phosphor-icons/react";
import { CircleNotch } from "@phosphor-icons/react/dist/ssr";
import { DialogTitle } from "@radix-ui/react-dialog";
import { useRouter } from "next/router";
import { useEffect, useMemo, useState } from "react";
import { Badge } from "../ui/badge";
import { Checkbox } from "../ui/checkbox";
import { Dialog, DialogClose, DialogContent } from "../ui/dialog";
import { Drawer, DrawerClose, DrawerContent, DrawerTitle } from "../ui/drawer";
import { Label } from "../ui/label";
import { RadioGroup, RadioGroupItem } from "../ui/radio-group";

const FilterSection = ({
  config,
  selectedItems,
  onChange,
  onApply,
  onReset,
}) => {
  const [openFilterDetail, setOpenFilterDetail] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const filteredItems = useMemo(() => {
    if (config.type !== "checkbox") return [];
    return config.data.filter((item) =>
      item.name.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [config.data, searchQuery, config.type]);

  const handleCheckboxChange = (item, checked) => {
    const updatedItems = checked
      ? [...selectedItems, item]
      : selectedItems.filter((i) => i.id !== item.id);
    onChange(updatedItems);
  };

  const handleRangeChange = (field, value) => {
    const updatedValue = {
      ...selectedItems,
      [field]: value ? Number(value) : undefined,
    };
    onChange(updatedValue);
  };

  if (config.type === "range") {
    return (
      <div className='divide-y divide-black/10'>
        <div>
          <div className='flex items-center justify-between py-2.5'>
            <span className='font-semibold'>{config.label}</span>
          </div>
          <div className='flex gap-2 pb-4'>
            <div className='relative'>
              <span className='absolute text-sm font-medium uppercase -translate-y-1/2 top-1/2 left-3'>
                USD
              </span>
              <input
                type='text'
                placeholder='Minimum'
                className='block w-full px-3 py-3 pl-12 text-sm bg-white border shadow-sm rounded-xl placeholder:text-background/50 disabled:text-background/40 text-background border-background/15 focus:outline-none'
                value={selectedItems.min || ""}
                onChange={(e) => handleRangeChange("min", e.target.value)}
              />
            </div>
            <div className='relative'>
              <span className='absolute text-sm font-medium uppercase -translate-y-1/2 top-1/2 left-3'>
                USD
              </span>
              <input
                type='text'
                placeholder='Maximum'
                className='block w-full px-3 py-3 pl-12 text-sm bg-white border shadow-sm rounded-xl placeholder:text-background/50 disabled:text-background/40 text-background border-background/15 focus:outline-none'
                value={selectedItems.max || ""}
                onChange={(e) => handleRangeChange("max", e.target.value)}
              />
            </div>
          </div>
          {(selectedItems.min || selectedItems.max) && (
            <div className='flex flex-wrap gap-1 pb-4'>
              <Badge
                className='flex items-center h-auto px-3 py-1 text-xs font-semibold transition-colors bg-white border rounded-full shadow-sm cursor-pointer whitespace-nowrap text-background border-black/10 hover:bg-white'
                onClick={() => onReset()}
              >
                {`$${selectedItems.min?.toLocaleString() || 0} - $${
                  selectedItems.max?.toLocaleString() || "∞"
                }`}
              </Badge>
            </div>
          )}
        </div>
      </div>
    );
  } else if (config.type === "checkbox") {
    return (
      <>
        <div className='divide-y divide-black/10'>
          <div>
            <div className='flex items-center justify-between py-2.5'>
              <span className='font-semibold'>{config.label}</span>
              <button
                type='button'
                onClick={() => setOpenFilterDetail(true)}
                className='inline-flex items-center gap-1.5 text-xs font-medium opacity-40'
              >
                View All
                <CaretRight />
              </button>
            </div>
            {selectedItems.length > 0 && (
              <div className='flex flex-wrap gap-1 pb-4'>
                {selectedItems.map((item) => (
                  <Badge
                    key={item.id}
                    className='flex items-center h-auto px-3 py-1 text-xs font-semibold transition-colors bg-white border rounded-full shadow-sm cursor-pointer whitespace-nowrap text-background border-black/10 hover:bg-white'
                    onClick={() => handleCheckboxChange(item, false)}
                  >
                    {item.name}
                  </Badge>
                ))}
              </div>
            )}
          </div>
        </div>

        <Dialog open={openFilterDetail} onOpenChange={setOpenFilterDetail}>
          <DialogContent
            className='flex flex-col gap-0 px-4 py-0 bg-white rounded-none h-dvh text-background'
            hideCloseButton
            aria-describedby={undefined}
          >
            <DialogTitle className='sr-only' />
            <div>
              <div className='flex items-center justify-between py-4 text-center'>
                <div className='flex items-center justify-start w-20'>
                  <button
                    type='button'
                    className='text-xs font-semibold text-red-500'
                    onClick={() => {
                      onReset();
                      setOpenFilterDetail(false);
                    }}
                  >
                    Clear Filter
                  </button>
                </div>
                <span className='flex-1 text-lg font-semibold tracking-tight'>
                  {config.label}
                </span>
                <div className='flex items-center justify-end w-20'>
                  <DialogClose asChild>
                    <button type='button'>
                      <X weight='bold' className='size-5 opacity-40' />
                    </button>
                  </DialogClose>
                </div>
              </div>
              <div className='relative'>
                <input
                  type='search'
                  placeholder={`Search by ${config.label.toLowerCase()} name`}
                  className='block w-full px-3 py-3 text-sm bg-white border shadow-sm rounded-xl placeholder:text-background/50 disabled:text-background/40 text-background pl-9 border-background/15 focus:outline-none'
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <MagnifyingGlass
                  className='absolute -translate-y-1/2 top-1/2 left-3 size-4 opacity-30'
                  weight='bold'
                />
              </div>
            </div>

            <div className='flex-1 py-5 overflow-y-auto no-scrollbar'>
              <div className='-my-2.5 divide-y divide-black/10 divide-dashed'>
                {filteredItems.map((item) => (
                  <div
                    key={item.id}
                    className='flex items-center w-full gap-3 py-2.5'
                  >
                    <Checkbox
                      id={`${config.key}-${item.id}`}
                      className='!size-5 rounded-md border-background/15 shadow data-[state=checked]:bg-orange-500 data-[state=checked]:text-white'
                      checked={selectedItems.some((i) => i.id === item.id)}
                      onCheckedChange={(checked) =>
                        handleCheckboxChange(item, checked)
                      }
                    />
                    <Label
                      htmlFor={`${config.key}-${item.id}`}
                      className='w-full text-base'
                    >
                      {item.name}
                    </Label>
                  </div>
                ))}
              </div>
            </div>

            {selectedItems.length !== 0 && (
              <div className='absolute inset-x-3 bottom-3'>
                <DialogClose asChild>
                  <button
                    type='button'
                    className='w-full py-2.5 font-medium text-white shadow-md rounded-xl bg-gradient-to-tl from-red-1 to-orange-1'
                  >
                    {`Apply ${selectedItems.length} item${
                      selectedItems.length > 1 ? "s" : ""
                    }`}
                  </button>
                </DialogClose>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </>
    );
  } else if (config.type === "radio") {
    return (
      <div className='divide-y divide-black/10'>
        <div>
          <div className='py-2.5'>
            <span className='font-semibold'>{config.label}</span>
            <RadioGroup
              value={selectedItems?.id || config.data[0].id} // Default to first option
              onValueChange={(value) => {
                const selectedOption = config.data.find(
                  (item) => item.id === value
                );
                console.log(`Selected ${config.key}:`, selectedOption);
                onChange(selectedOption);
              }}
              className='flex flex-wrap items-center gap-4 mt-2.5 pb-1'
            >
              {config.data.map((item) => (
                <div key={item.id} className='flex items-center gap-2'>
                  <RadioGroupItem
                    value={item.id}
                    id={`${config.key}-${item.id}`}
                    className='!size-[18px] rounded-full border-background/15 shadow data-[state=checked]:bg-orange-500 data-[state=checked]:text-white'
                  />
                  <Label
                    htmlFor={`${config.key}-${item.id}`}
                    className='text-sm font-medium'
                  >
                    {item.name}
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </div>
        </div>
      </div>
    );
  } else {
    console.warn(`Invalid filter type for ${config.key}: ${config.type}`);
    return null;
  }
};

const FilterMobile = ({
  filterConfigs,
  initialQuery = {},
  redirectUrl,
  title,
  children,
}) => {
  const router = useRouter();
  const [openDrawer, setOpenDrawer] = useState(false);
  const [loading, setLoading] = useState(false);

  const [filters, setFilters] = useState(
    filterConfigs.reduce((acc, config) => {
      if (config.type === "range") {
        acc[config.key] = {
          min: initialQuery[config.minQueryKey]
            ? Number(initialQuery[config.minQueryKey])
            : undefined,
          max: initialQuery[config.maxQueryKey]
            ? Number(initialQuery[config.maxQueryKey])
            : undefined,
        };
      } else if (config.type === "checkbox") {
        acc[config.key] = initialQuery[config.queryKey]
          ? config.data.filter((item) =>
              initialQuery[config.queryKey].split(",").includes(String(item.id))
            )
          : [];
      } else if (config.type === "radio") {
        acc[config.key] = initialQuery[config.queryKey]
          ? config.data.find(
              (item) =>
                String(item.value) === String(initialQuery[config.queryKey])
            )
          : config.data[0]; // Default to first option
      }
      return acc;
    }, {})
  );

  const [temps, setTemps] = useState(
    filterConfigs.reduce((acc, config) => {
      if (config.type === "range") {
        acc[config.key] = {
          min: initialQuery[config.minQueryKey]
            ? Number(initialQuery[config.minQueryKey])
            : undefined,
          max: initialQuery[config.maxQueryKey]
            ? Number(initialQuery[config.maxQueryKey])
            : undefined,
        };
      } else if (config.type === "checkbox") {
        acc[config.key] = initialQuery[config.queryKey]
          ? config.data.filter((item) =>
              initialQuery[config.queryKey].split(",").includes(String(item.id))
            )
          : [];
      } else if (config.type === "radio") {
        acc[config.key] = initialQuery[config.queryKey]
          ? config.data.find(
              (item) =>
                String(item.value) === String(initialQuery[config.queryKey])
            )
          : config.data[0];
      }
      return acc;
    }, {})
  );

  useEffect(() => {
    setFilters(
      filterConfigs.reduce((acc, config) => {
        if (config.type === "range") {
          acc[config.key] = {
            min: initialQuery[config.minQueryKey]
              ? Number(initialQuery[config.minQueryKey])
              : undefined,
            max: initialQuery[config.maxQueryKey]
              ? Number(initialQuery[config.maxQueryKey])
              : undefined,
          };
        } else if (config.type === "checkbox") {
          acc[config.key] = initialQuery[config.queryKey]
            ? config.data.filter((item) =>
                initialQuery[config.queryKey]
                  .split(",")
                  .includes(String(item.id))
              )
            : [];
        } else if (config.type === "radio") {
          acc[config.key] = initialQuery[config.queryKey]
            ? config.data.find(
                (item) =>
                  String(item.value) === String(initialQuery[config.queryKey])
              )
            : config.data[0];
        }
        return acc;
      }, {})
    );
    setTemps(
      filterConfigs.reduce((acc, config) => {
        if (config.type === "range") {
          acc[config.key] = {
            min: initialQuery[config.minQueryKey]
              ? Number(initialQuery[config.minQueryKey])
              : undefined,
            max: initialQuery[config.maxQueryKey]
              ? Number(initialQuery[config.maxQueryKey])
              : undefined,
          };
        } else if (config.type === "checkbox") {
          acc[config.key] = initialQuery[config.queryKey]
            ? config.data.filter((item) =>
                initialQuery[config.queryKey]
                  .split(",")
                  .includes(String(item.id))
              )
            : [];
        } else if (config.type === "radio") {
          acc[config.key] = initialQuery[config.queryKey]
            ? config.data.find(
                (item) =>
                  String(item.value) === String(initialQuery[config.queryKey])
              )
            : config.data[0];
        }
        return acc;
      }, {})
    );
  }, [initialQuery, filterConfigs]);

  const handleFindStartup = () => {
    console.log("Opening filter drawer");
    setOpenDrawer(true);
  };

  const clearAllFilters = () => {
    setTemps(
      filterConfigs.reduce((acc, config) => {
        if (config.type === "range") {
          acc[config.key] = { min: undefined, max: undefined };
        } else if (config.type === "checkbox") {
          acc[config.key] = [];
        } else if (config.type === "radio") {
          acc[config.key] = config.data[0]; // Default to first option
        }
        return acc;
      }, {})
    );
    setFilters(
      filterConfigs.reduce((acc, config) => {
        if (config.type === "range") {
          acc[config.key] = { min: undefined, max: undefined };
        } else if (config.type === "checkbox") {
          acc[config.key] = [];
        } else if (config.type === "radio") {
          acc[config.key] = config.data[0];
        }
        return acc;
      }, {})
    );
  };

  const applyFilters = async () => {
    if (!router.isReady) {
      console.warn("Router is not ready, delaying navigation");
      return;
    }
    setLoading(true);
    setFilters(temps);
    const query = {};
    filterConfigs.forEach((config) => {
      if (config.type === "range") {
        if (temps[config.key].min !== undefined) {
          query[config.minQueryKey] = temps[config.key].min;
        }
        if (temps[config.key].max !== undefined) {
          query[config.maxQueryKey] = temps[config.key].max;
        }
      } else if (config.type === "checkbox") {
        if (temps[config.key].length > 0) {
          query[config.queryKey] = temps[config.key]
            .map((item) => item.id)
            .join(",");
        }
      } else if (config.type === "radio") {
        if (temps[config.key].value !== null) {
          query[config.queryKey] = temps[config.key].value;
        }
      }
    });
    console.log(
      "Applying filters with redirectUrl:",
      redirectUrl,
      "query:",
      query
    );
    if (!redirectUrl) {
      console.error("redirectUrl is undefined or empty");
      setLoading(false);
      return;
    }
    try {
      await router.push({
        pathname: redirectUrl,
        query,
      });
      setOpenDrawer(false);
    } catch (error) {
      console.error("Error during navigation:", error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilter = (key) => {
    setFilters((curr) => ({ ...curr, [key]: temps[key] }));
  };

  const removeFilter = (key) => {
    const config = filterConfigs.find((c) => c.key === key);
    setTemps((curr) => ({
      ...curr,
      [key]:
        config.type === "range"
          ? { min: undefined, max: undefined }
          : config.type === "radio"
          ? config.data[0]
          : [],
    }));
    setFilters((curr) => ({
      ...curr,
      [key]:
        config.type === "range"
          ? { min: undefined, max: undefined }
          : config.type === "radio"
          ? config.data[0]
          : [],
    }));
  };

  return (
    <>
      {children && children({ openDrawer: handleFindStartup })}

      <Drawer
        open={openDrawer}
        onOpenChange={setOpenDrawer}
        dismissible={!loading}
      >
        <DrawerContent
          className='p-4 pt-0 bg-gray-50 text-background rounded-t-3xl'
          aria-describedby={undefined}
          showHandle={false}
        >
          <DrawerTitle />
          <div className='flex items-center justify-between pb-3 mt-4 text-center border-b border-black/10'>
            <div className='flex items-center justify-start w-12'>
              <button
                type='button'
                className='text-xs font-semibold text-red-500'
                onClick={clearAllFilters}
                disabled={loading}
              >
                Reset
              </button>
            </div>
            <span className='flex-1 text-lg font-semibold tracking-tight'>
              {title}
            </span>
            <div className='flex items-center justify-end w-12'>
              <DrawerClose asChild>
                <button type='button' disabled={loading}>
                  <X weight='bold' className='size-5 opacity-40' />
                </button>
              </DrawerClose>
            </div>
          </div>
          <div className='max-h-[85dvh] overflow-y-auto pt-1.5 no-scrollbar'>
            <div className='divide-y divide-black/10'>
              {filterConfigs.map((config) => (
                <FilterSection
                  key={config.key}
                  config={config}
                  selectedItems={
                    temps[config.key] ||
                    (config.type === "range"
                      ? { min: undefined, max: undefined }
                      : config.type === "radio"
                      ? config.data[0]
                      : [])
                  }
                  onChange={(items) =>
                    setTemps((curr) => ({ ...curr, [config.key]: items }))
                  }
                  onApply={() => applyFilter(config.key)}
                  onReset={() => removeFilter(config.key)}
                />
              ))}
            </div>
            <button
              type='button'
              className='flex items-center justify-center w-full gap-2 py-2.5 mt-1 text-sm font-medium text-white shadow-md rounded-xl bg-gradient-to-tl from-red-1 to-orange-1'
              onClick={applyFilters}
              disabled={loading}
            >
              {loading && (
                <CircleNotch weight='bold' className='animate-spin size-4' />
              )}
              {loading ? "Applying filters, please wait..." : "Apply Filter"}
            </button>
          </div>
        </DrawerContent>
      </Drawer>
    </>
  );
};

export default FilterMobile;
